import React, { useState } from 'react';
import { parseNumberInput } from '../../utils/lionAutoNumber';

// Number input that lets the pricer clear and retype: it keeps its own text
// and only reports values that pass the bounds (parseNumberInput). With
// `blankValue` an empty box is a value too (reported as blankValue, which
// shows as an empty box): the floor's "blank = use the show's / artist's
// floor". The browser's bad input (a type="number" box holding "1e" reads as
// blank) is never reported, not even as blank: the box shows red and keeps
// its last value, so a typo never clears the floor. `ariaDescribedBy` ties
// the input to a hint's id.
function NumberField({
  value, onChange, min = 0, max, integer = false, step, title, prefix, suffix, blankValue, placeholder, ariaLabel, ariaDescribedBy,
}) {
  const blankable = blankValue !== undefined;
  const display = (v) => (blankable && v === blankValue ? '' : String(v ?? 0));
  const [text, setText] = useState(() => display(value));
  const [prevValue, setPrevValue] = useState(value);
  const [badInput, setBadInput] = useState(false);
  // Bumped to remount the input: the only way to clear the browser's bad
  // input (its value already reads blank, so setting '' changes nothing)
  const [inputKey, setInputKey] = useState(0);
  const parse = (raw, isBad = false) => parseNumberInput(raw, { min, max, integer, blankValue, badInput: isBad });
  // Follow outside changes (method switch, another group, a preset button)
  // without clobbering what is being typed when it already means the same
  // number
  if (value !== prevValue) {
    setPrevValue(value);
    const same = !badInput && (blankable ? parse(text) === value : Number(text) === value && text.trim() !== '');
    if (!same) {
      setText(display(value));
      if (badInput) {
        setBadInput(false);
        setInputKey(k => k + 1);
      }
    }
  }
  const bad = badInput || parse(text) === null;
  return (
    <span className="la-number">
      {prefix}
      <input
        key={inputKey}
        type="number"
        className={`form-input form-input-sm${bad ? ' la-input-bad' : ''}`}
        value={text}
        min={min}
        max={max}
        step={step || (integer ? 1 : 'any')}
        title={title}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        onChange={(e) => {
          const isBad = !!e.target.validity?.badInput;
          setText(e.target.value);
          setBadInput(isBad);
          const n = parse(e.target.value, isBad);
          if (n !== null && n !== value) onChange(n);
        }}
      />
      {suffix}
    </span>
  );
}

export default NumberField;
